import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './SecureContact.css';

function SecureContact() {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStep, setSubmissionStep] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Auto-typing intro
  const [introLines, setIntroLines] = useState([]);
  const [isTypingComplete, setIsTypingComplete] = useState(false);

  // Ref for scrolling
  const messagesEndRef = useRef(null);

  // Auto-typing messages on component mount
  useEffect(() => {
    const messages = [
      '> INITIALIZING SECURE CHANNEL...',
      '> VERIFYING IDENTITY PROTOCOLS...',
      '> CONNECTION ESTABLISHED ✓',
      '> Ready to receive transmission.'
    ];

    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex < messages.length) {
        setIntroLines(prev => [...prev, messages[currentIndex]]);
        currentIndex++;
      } else {
        setIsTypingComplete(true);
        clearInterval(interval);
      }
    }, 200);

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom when new intro lines appear (only within window content)
  useEffect(() => {
    if (messagesEndRef.current) {
      const element = messagesEndRef.current;
      const container = element.closest('.window-content');
      if (container) {
        // Scroll only within the window content, not the entire page
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [introLines, submissionStep]);

  // Form input handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validation
  const validate = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = '✗ ERROR: Identity field cannot be empty';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = '✗ ERROR: Identity must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = '✗ ERROR: Contact endpoint required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = '✗ ERROR: Invalid contact endpoint format';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = '✗ ERROR: Message payload cannot be empty';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = '✗ ERROR: Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Simulate transmission steps
  const showTransmissionStep = (message, delay) => {
    return new Promise(resolve => {
      setTimeout(() => {
        setSubmissionStep(message);
        resolve();
      }, delay);
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Step 1: Encrypting
      await showTransmissionStep('> Encrypting message with AES-256... ✓', 100);

      // Step 2: Packetizing
      await showTransmissionStep('> Packetizing data... ✓', 1000);

      // Step 3: Establishing connection
      await showTransmissionStep('> Establishing secure tunnel... ✓', 1800);

      // Step 4: Transmitting
      await showTransmissionStep('> Transmitting... [████████] 100%', 2600);

      // Actually send email via EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        to_name: 'Diptanshu Dhawan'
      };

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID',
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY'
      );

      // Success!
      setIsSuccess(true);
      setSubmissionStep('> ✅ TRANSMISSION SUCCESSFUL');
      
      // Clear form
      setFormData({ name: '', email: '', message: '' });

    } catch (error) {
      setSubmitError('✗ TRANSMISSION FAILED: Unable to establish secure connection. Please try again.');
      setSubmissionStep('');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle download resume
  const handleDownloadResume = () => {
    const link = document.createElement('a');
    link.href = '/resume.pdf';
    link.download = 'Diptanshu_Dhawan_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Social links data
  const socialLinks = [
    { 
      name: 'GitHub', 
      url: 'https://github.com/DiptanshuDhawan',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
        </svg>
      ),
      color: '#ffffff'
    },
    { 
      name: 'LinkedIn', 
      url: 'https://www.linkedin.com/in/diptanshudhawan',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      color: '#0077b5'
    },
    { 
      name: 'YouTube', 
      url: 'https://www.youtube.com/@HackWithDD',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
      color: '#FF0000'
    },
    { 
      name: 'TryHackMe', 
      url: 'https://tryhackme.com/p/diptanshudhawan',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M10.705 0C7.54 0 4.902 2.285 4.349 5.291a4.525 4.525 0 0 0-4.107 4.5 4.525 4.525 0 0 0 4.52 4.52h6.761a.625.625 0 1 0 0-1.25H4.761a3.273 3.273 0 0 1-3.27-3.27 3.273 3.273 0 0 1 3.027-3.257.625.625 0 0 0 .545-.594c.015-2.963 2.447-5.69 5.642-5.69 3.225 0 5.356 2.777 5.356 5.527v.875a.624.624 0 0 0 .625.625c1.388 0 2.518 1.13 2.518 2.518s-1.13 2.52-2.518 2.52h-.77a.625.625 0 1 0 0 1.25h.77c2.11 0 3.768-1.658 3.768-3.77s-1.658-3.768-3.768-3.768V5.5c0-3.096-2.527-6.75-6.606-6.75 0 0 .27 0 0 0zm4.028 12.625h-.77a.624.624 0 1 0 0 1.25h.77a2.52 2.52 0 0 0 2.518-2.518 2.52 2.52 0 0 0-2.518-2.52.625.625 0 0 1-.625-.624V5.5c0-2.75-2.131-5.527-5.356-5.527-3.195 0-5.627 2.727-5.642 5.69a.625.625 0 0 1-.545.594 3.273 3.273 0 0 0-3.027 3.257 3.273 3.273 0 0 0 3.27 3.27h6.761a.625.625 0 1 1 0 1.25H4.761a4.525 4.525 0 0 1-4.52-4.52 4.525 4.525 0 0 1 4.107-4.5C4.902 2.285 7.54 0 10.705 0c4.08 0 6.606 3.654 6.606 6.75v2.463a3.768 3.768 0 0 1 3.768 3.768c0 2.112-1.658 3.77-3.768 3.77h-.578z"/>
        </svg>
      ),
      color: '#C11111'
    },
    { 
      name: 'Instagram', 
      url: 'https://www.instagram.com/i.diptanshu/',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
        </svg>
      ),
      color: '#E4405F'
    },
  ];

  return (
    <div className="profile-card contact-card">
      <div className="card-header">
        <h3>📬 Secure Contact Channel</h3>
      </div>

      <div className="secure-contact">
        {/* Terminal-style intro */}
        <div className="terminal-output">
          {introLines.map((line, index) => (
            <div key={index} className="terminal-line">
              {line}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Form - only show when typing is complete */}
        {isTypingComplete && !isSuccess && (
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name" className="terminal-label">[IDENTITY]</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                className="terminal-input"
                disabled={isSubmitting}
              />
              {errors.name && <div className="error-message">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="email" className="terminal-label">[CONTACT]</label>
              <input
                type="email"
id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="terminal-input"
                disabled={isSubmitting}
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="message" className="terminal-label">[MESSAGE]</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your message..."
                className="terminal-textarea"
                rows="4"
                disabled={isSubmitting}
              />
              {errors.message && <div className="error-message">{errors.message}</div>}
            </div>

            <button 
              type="submit" 
              className="submit-button" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span>⚡ TRANSMITTING...</span>
              ) : (
                <span>▶ SEND ENCRYPTED MESSAGE</span>
              )}
            </button>

            {submitError && <div className="error-message">{submitError}</div>}
          </form>
        )}

        {/* Transmission animation */}
        {isSubmitting && submissionStep && (
          <div className="transmission-status">
            <div className="terminal-line">{submissionStep}</div>
          </div>
        )}

        {/* Success message */}
        {isSuccess && (
          <div className="success-message">
            <div className="terminal-line success-line">{submissionStep}</div>
            <div className="terminal-line">
              {'>'} Message delivered successfully.
            </div>
            <div className="terminal-line">
              {'>'} Expect response within 24-48 hours.
            </div>
            <button 
              className="reset-button" 
              onClick={() => {
                setIsSuccess(false);
                setSubmissionStep('');
              }}
            >
              ↻ SEND ANOTHER MESSAGE
            </button>
          </div>
        )}

        {/* Social Links - Always visible */}
        {isTypingComplete && (
          <div className="quick-contact">
            <div className="divider">
              <span>QUICK LINKS</span>
            </div>
            
            <div className="social-grid">
              {socialLinks.map((social, index) => (
                <a 
                  key={index}
                  href={social.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-button"
                  style={{'--social-color': social.color}}
                  title={social.name}
                >
                  <span className="social-icon">{social.icon}</span>
                  <span className="social-label">{social.name}</span>
                </a>
              ))}
            </div>

            <div className="email-direct">
              <a href="mailto:dhawandiptanshu.work@gmail.com" className="email-link">
                <span className="link-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                </span>
                <span className="link-text">dhawandiptanshu.work@gmail.com</span>
              </a>
            </div>

            <button onClick={handleDownloadResume} className="resume-button">
              <span className="btn-icon">📄</span>
              <span className="btn-text">Download Resume</span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SecureContact;
